// App notification center — one bell for the whole product, cloud-console
// style. Strict priority ordering: "Needs your action" pinned on top, then
// "In progress", then "Activity". Today's sources are EA Connect (upload
// extraction tracking + instance lifecycle transitions); new product areas
// plug in as additional sources. The source watchers live here because the
// header is mounted everywhere.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import { Badge, Box, Button, ClickAwayListener, Paper, Popper, Stack, Typography, useMediaQuery } from '@mui/material';
import { BellOutlined, CheckCircleFilled, CloseOutlined, CloudServerOutlined, MessageOutlined, WarningFilled } from '@ant-design/icons';

import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import { HEADER_ICON_BUTTON_SX, HEADER_ICON_SLOT_SX } from 'components/ui/tokens';
import Transitions from 'components/@extended/Transitions';
import { useEAHostingClient } from 'client/client-hook';
import { DispatchSnackbarService } from 'services';
import { useDispatch } from 'store';
import useAuth from 'hooks/useAuth';
import { hasEaConnectAccess } from 'utils/eaConnect';
import { MONO, PlatformBadge, tnum } from 'sections/ea-connect/ui';
import { selectUnseenDone, selectVisibleJobs, useExtractionStore, ExtractionJob } from 'sections/ea-connect/useExtractionStore';

const service = new DispatchSnackbarService();

// ---- Background watcher (runs wherever the indicator is mounted: the header) ----
//
// The server (ea-manager ListUploads) is the source of truth: one RPC returns
// every tracked upload with live extraction state, so progress follows the
// account across sessions and devices. Poll fast while something is in
// flight, slow when idle, and announce transitions with a snackbar.

const POLL_ACTIVE_MS = 5000;
const POLL_IDLE_MS = 60000;

const useExtractionWatcher = (enabled: boolean) => {
  const getClient = useEAHostingClient();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const userId = user?.tcId || '';
  const setFromServer = useExtractionStore((s) => s.setFromServer);
  const hasActive = useExtractionStore((s) => Object.values(s.jobs).some((j) => j.status === 'extracting'));
  const prevStatusRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!enabled || !userId) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const client = await getClient();
        const res = await client.listUploads(userId);
        if (cancelled) return;
        const entries = res.uploadsList || [];
        // Announce extracting -> terminal transitions observed by this tab.
        entries.forEach((e) => {
          const prev = prevStatusRef.current[e.sha256];
          if (prev === 'extracting' && e.status === 'ready') {
            service.dispatchSnackbar(
              dispatch,
              `${e.fileName}: ${e.inputCount} input${e.inputCount === 1 ? '' : 's'} extracted — ready to configure`,
              'alert',
              'success',
              6000
            );
          }
          if (prev === 'extracting' && e.status === 'failed') {
            service.dispatchSnackbar(dispatch, `Could not read inputs from ${e.fileName} — provide a .set file`, 'alert', 'warning', 6000);
          }
          prevStatusRef.current[e.sha256] = e.status;
        });
        setFromServer(entries);
      } catch {
        // transient — next tick retries
      }
    };

    tick();
    const id = window.setInterval(tick, hasActive ? POLL_ACTIVE_MS : POLL_IDLE_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled, userId, hasActive, getClient, dispatch, setFromServer]);
};

// ---- Instance activity (light poll; header is always mounted) ----

type ActivityItem = {
  key: string;
  atSec: number;
  kind: 'error' | 'lifecycle';
  title: string;
  detail: string;
  instanceId: string;
};

const ACTIVITY_POLL_MS = 30000;
const ACTIVITY_KEEP = 8;

const useInstanceActivity = (enabled: boolean) => {
  const getClient = useEAHostingClient();
  const { user } = useAuth();
  const userId = user?.tcId || '';
  const [items, setItems] = useState<ActivityItem[]>([]);
  const prevRef = useRef<Record<string, string> | null>(null);

  useEffect(() => {
    if (!enabled || !userId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const client = await getClient();
        const res = await client.listInstances(userId);
        if (cancelled) return;
        const now = Math.floor(Date.now() / 1000);
        const cur: Record<string, string> = {};
        const fresh: ActivityItem[] = [];
        (res.instancesList || []).forEach((i) => {
          // The states that mean "this bot is not making money" must be
          // distinguishable here. login_failed and connection_lost used to fall
          // into the catch-all 'starting' bucket and were announced as
          // "spinning up — Provisioning on your slot", which is the opposite of
          // what they mean.
          const st =
            i.status === 'running'
              ? 'running'
              : i.status === 'failed' || i.status === 'error' || i.status === 'crashed' || i.status === 'login_failed'
              ? 'error'
              : i.status === 'connection_lost'
              ? 'degraded'
              : i.status === 'updating_settings'
              ? 'applying'
              : i.status === 'stopped'
              ? 'stopped'
              : 'starting';
          cur[i.id] = st;
          const prev = prevRef.current?.[i.id];
          // First poll seeds silently; only TRANSITIONS notify.
          if (prevRef.current && prev !== st) {
            const name = i.name || i.id;
            // login_failed is the agent's terminal give-up (broker link never
            // came back across its restart budget) — it needs the user, not a
            // generic "hit an error".
            const copy: Record<string, { title: string; detail: string }> = {
              error:
                i.status === 'login_failed'
                  ? {
                      title: `${name} can't reach its trading account`,
                      detail: 'The terminal could not log in after several retries — check the broker account, then restart the bot.'
                    }
                  : { title: `${name} hit an error`, detail: 'The instance stopped unexpectedly — open its journal.' },
              degraded: {
                title: `${name} lost its broker link`,
                detail: 'No trade server connection — the terminal is reconnecting on its own.'
              },
              applying: { title: `${name} is applying new settings`, detail: 'The terminal restarts to reload your inputs.' },
              running: { title: `${name} is running`, detail: 'The terminal attached your EA.' },
              stopped: { title: `${name} is stopped`, detail: 'Paused — no new trades.' },
              starting: { title: `${name} is spinning up`, detail: 'Provisioning on your slot.' }
            };
            const c = copy[st] || copy.starting;
            fresh.push({
              key: `${i.id}-${now}-${st}`,
              atSec: now,
              // Only a terminal failure is user-actionable; a reconnecting bot
              // is worth telling them about but not worth a red action row.
              kind: st === 'error' ? 'error' : 'lifecycle',
              title: c.title,
              detail: c.detail,
              instanceId: i.id
            });
          }
        });
        prevRef.current = cur;
        if (fresh.length) setItems((old) => [...fresh, ...old].slice(0, ACTIVITY_KEEP));
      } catch {
        // transient — next tick retries
      }
    };
    tick();
    const id = window.setInterval(tick, ACTIVITY_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled, userId, getClient]);

  const dismissItem = (key: string) => setItems((old) => old.filter((i) => i.key !== key));
  return { items, dismissItem };
};

// ---- Job row ----

const JobRow = ({
  job,
  onAction,
  onDismiss
}: {
  job: ExtractionJob;
  onAction: (j: ExtractionJob) => void;
  onDismiss: (j: ExtractionJob) => void;
}) => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{ px: 2, py: 1.5, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}
    >
      {job.status === 'extracting' ? (
        <Box
          aria-hidden
          sx={{
            width: 14,
            height: 14,
            flexShrink: 0,
            borderRadius: '50%',
            border: `2px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            borderTopColor: theme.palette.primary.main,
            animation: 'eaXSpin .9s linear infinite',
            '@keyframes eaXSpin': { to: { transform: 'rotate(360deg)' } },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
          }}
        />
      ) : job.status === 'ready' ? (
        <Box sx={{ display: 'flex', color: 'success.main', fontSize: 15, flexShrink: 0 }}>
          <CheckCircleFilled />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', color: 'warning.main', fontSize: 15, flexShrink: 0 }}>
          <WarningFilled />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography noWrap sx={{ fontFamily: MONO, fontSize: '0.75rem', fontWeight: 600 }}>
            {job.fileName}
          </Typography>
          <PlatformBadge platform={job.platform} />
        </Stack>
        <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', ...tnum }}>
          {job.status === 'extracting'
            ? 'extracting inputs…'
            : job.status === 'ready'
            ? `${job.inputCount ?? 0} input${job.inputCount === 1 ? '' : 's'} extracted`
            : job.error || 'extraction failed'}
        </Typography>
      </Box>
      {job.status !== 'extracting' && (
        <Button
          size="small"
          variant={job.status === 'ready' ? 'contained' : 'outlined'}
          color={job.status === 'ready' ? 'primary' : 'warning'}
          onClick={() => onAction(job)}
        >
          {job.status === 'ready' ? 'Configure' : 'Resume'}
        </Button>
      )}
      <IconButton size="small" color="secondary" onClick={() => onDismiss(job)} aria-label="dismiss">
        <CloseOutlined style={{ fontSize: 11 }} />
      </IconButton>
    </Stack>
  );
};

// ---- Indicator ----

type NotificationCenterProps = {
  // Unread live-chat (support) messages — injected by the header, which owns
  // the Crisp wiring. Support replies are user-actionable by definition, so
  // they render in "Needs your action" and count toward the badge.
  chatUnread?: number;
  onOpenChat?: () => void;
};

const NotificationCenter = ({ chatUnread = 0, onOpenChat }: NotificationCenterProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);
  const getClient = useEAHostingClient();
  const { user } = useAuth();
  const userId = user?.tcId || '';

  // Only role-holders can call these RPCs — for everyone else this renders
  // nothing and polls nothing.
  const entitled = hasEaConnectAccess(user?.roles);
  useExtractionWatcher(entitled);
  const { items: activity, dismissItem } = useInstanceActivity(entitled);

  const visible = useExtractionStore(selectVisibleJobs);
  const unseenDone = useExtractionStore(selectUnseenDone);
  const markSeen = useExtractionStore((s) => s.markSeen);
  const removeLocal = useExtractionStore((s) => s.removeLocal);
  const extracting = visible.filter((j) => j.status === 'extracting');

  // Priority split, cloud-console style: user-actionable items pinned first.
  const actionJobs = visible.filter((j) => j.status !== 'extracting');
  const errorActivity = activity.filter((a) => a.kind === 'error');
  const infoActivity = activity.filter((a) => a.kind !== 'error');
  const actionCount = actionJobs.length + errorActivity.length + (chatUnread > 0 ? 1 : 0);
  const totalVisible = visible.length + activity.length + (chatUnread > 0 ? 1 : 0);

  // Always visible: this is the app's notification center. Product-area
  // sources gate their own polling (EA watchers only run for role-holders);
  // everyone else simply sees the caught-up state until other areas plug in.

  const goConfigure = (job: ExtractionJob) => {
    markSeen(job.sha256);
    setOpen(false);
    navigate(`/ea-connect/deploy?artifact=${job.sha256}`);
  };

  // Dismiss is server-side: the row disappears from EVERY session's tracker.
  // Remove locally first so the UI answers instantly.
  const dismiss = (job: ExtractionJob) => {
    removeLocal(job.sha256);
    getClient()
      .then((c) => c.dismissUpload(userId, job.sha256))
      .catch(() => undefined);
  };

  const sectionLabel = (label: string) => (
    <Typography
      sx={{
        px: 2,
        pt: 1.25,
        pb: 0.5,
        fontSize: '0.625rem',
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: 'text.secondary'
      }}
    >
      {label}
    </Typography>
  );

  const activityRow = (a: ActivityItem) => (
    <Stack
      key={a.key}
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{ px: 2, py: 1.25, cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}
      onClick={() => {
        setOpen(false);
        navigate(`/ea-connect/fleet/${a.instanceId}`);
      }}
    >
      <Box sx={{ display: 'flex', fontSize: 15, flexShrink: 0, color: a.kind === 'error' ? 'error.main' : 'text.secondary' }}>
        {a.kind === 'error' ? <WarningFilled /> : <CloudServerOutlined />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
          {a.title}
        </Typography>
        <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>{a.detail}</Typography>
      </Box>
      <IconButton
        size="small"
        color="secondary"
        aria-label="dismiss"
        onClick={(e) => {
          e.stopPropagation();
          dismissItem(a.key);
        }}
      >
        <CloseOutlined style={{ fontSize: 11 }} />
      </IconButton>
    </Stack>
  );

  return (
    <Box sx={HEADER_ICON_SLOT_SX}>
      <IconButton
        color="secondary"
        sx={HEADER_ICON_BUTTON_SX}
        aria-label="notifications"
        ref={anchorRef}
        onClick={() => setOpen((o) => !o)}
      >
        <Badge
          badgeContent={unseenDone.length + errorActivity.length || null}
          color={unseenDone.some((j) => j.status === 'failed') || errorActivity.length > 0 ? 'warning' : 'success'}
          variant={unseenDone.length + errorActivity.length ? 'standard' : undefined}
        >
          <Box sx={{ position: 'relative', display: 'flex', fontSize: 18 }}>
            <BellOutlined />
            {extracting.length > 0 && (
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  inset: -5,
                  borderRadius: '50%',
                  border: `1.5px solid ${alpha(theme.palette.primary.main, 0.6)}`,
                  borderTopColor: 'transparent',
                  animation: 'eaXOrbit 1.4s linear infinite',
                  '@keyframes eaXOrbit': { to: { transform: 'rotate(360deg)' } },
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none', borderTopColor: alpha(theme.palette.primary.main, 0.6) }
                }}
              />
            )}
          </Box>
        </Badge>
      </IconButton>
      <Popper
        placement={matchesXs ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [matchesXs ? -5 : 0, 9] } }] }}
        sx={{ zIndex: theme.zIndex.modal }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={{ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 340, maxWidth: 420 }}>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <MainCard elevation={0} border={false} content={false}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.border.main}` }}
                  >
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700 }}>Notifications</Typography>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', ...tnum }}>
                      {extracting.length > 0
                        ? `${extracting.length} extracting`
                        : actionCount > 0
                        ? `${actionCount} need${actionCount === 1 ? 's' : ''} your action`
                        : 'up to date'}
                    </Typography>
                  </Stack>

                  {/* user-actionable first, always */}
                  {(actionJobs.length > 0 || errorActivity.length > 0 || chatUnread > 0) && sectionLabel('Needs your action')}
                  {chatUnread > 0 && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.25}
                      sx={{ px: 2, py: 1.25, cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}
                      onClick={() => {
                        setOpen(false);
                        onOpenChat?.();
                      }}
                    >
                      <Box sx={{ display: 'flex', fontSize: 15, flexShrink: 0, color: 'primary.main' }}>
                        <MessageOutlined />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                          {chatUnread} new support {chatUnread === 1 ? 'message' : 'messages'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>Open live chat to reply.</Typography>
                      </Box>
                      <Button size="small" variant="contained">
                        Open chat
                      </Button>
                    </Stack>
                  )}
                  {actionJobs.map((j) => (
                    <JobRow key={j.sha256} job={j} onAction={goConfigure} onDismiss={dismiss} />
                  ))}
                  {errorActivity.map(activityRow)}

                  {/* in-flight extractions */}
                  {extracting.length > 0 && sectionLabel('In progress')}
                  {extracting.map((j) => (
                    <JobRow key={j.sha256} job={j} onAction={goConfigure} onDismiss={dismiss} />
                  ))}

                  {/* everything else */}
                  {infoActivity.length > 0 && sectionLabel('Activity')}
                  {infoActivity.map(activityRow)}

                  {totalVisible === 0 && (
                    <Stack alignItems="center" spacing={0.75} sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', fontSize: 22, color: 'success.main' }}>
                        <CheckCircleFilled />
                      </Box>
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>You&apos;re all caught up</Typography>
                      <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
                        Activity from across the app lands here.
                      </Typography>
                    </Stack>
                  )}
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default NotificationCenter;
